import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Input,
  Button,
  Toggle,
  Typography,
  BackArrow
} from '../../../components';
import affiliateService from '../../../api/affiliateService';
import { useUi } from '../../../context/ui.context';
import { usePageTitle } from '../../../context/navigationContext';
import { useTranslation } from 'react-i18next';

// Types for the affiliate settings
type AffiliateSettings = {
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  manual_referrals_accept: boolean;
  signup_bonus: boolean;
  signup_bonus_value: string;
  percent_payment: string;
  signup_required_credit_card?: boolean;
  signup_required_days?: number | null;
  signup_minimal_payment?: number | null;
  percent_minimal_payment?: number | null;
};

// Validation schema
const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  description: yup.string().required('Description is required'),
  signup_bonus_value: yup.string()
    .when('signup_bonus', {
      is: true,
      then: (schema) => schema.required('Signup bonus value is required when signup bonus is enabled'),
      otherwise: (schema) => schema
    }),
  signup_required_days: yup.number()
    .nullable()
    .min(1, 'Required days must be >= 1'),
  signup_minimal_payment: yup.number()
    .nullable()
    .min(0, 'Minimal payment must be >= 0'),
  percent_payment: yup.string()
    .required('Percent payment is required'),
  percent_minimal_payment: yup.number()
    .nullable()
    .min(0, 'Percent minimal payment must be >= 0'),
});

const CreateEditAffiliateSettings = () => {
  const [loading, setLoading] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { viewAlert } = useUi();
  const [t] = useTranslation();
  usePageTitle(isEdit ? t("EDIT_AFFILIATE_SETTINGS") : t("CREATE_AFFILIATE_SETTINGS"));

  const formik = useFormik<AffiliateSettings>({
    initialValues: {
      name: '',
      description: '',
      is_default: false,
      is_active: true,
      manual_referrals_accept: false,
      signup_bonus: false,
      signup_bonus_value: '0.00',
      percent_payment: '0.00',
      signup_required_credit_card: false,
      signup_required_days: null,
      signup_minimal_payment: null,
      percent_minimal_payment: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log('Submit - isEdit:', isEdit, 'id:', id); // Debug info
      setLoading(true);
      try {
        if (isEdit && id) {
          console.log('Editing setting with ID:', id, 'data:', values); // Debug info
          const dataWithId = { ...values, id };
          await affiliateService.editSettings(dataWithId, id);
          setLoadingComplete(true);
          viewAlert({
            severity: 'information',
            message: 'Settings updated successfully!'
          });
        } else {
          console.log('Creating new setting with data:', values); // Debug info
          await affiliateService.createSettings(values);
          setLoadingComplete(true);
          viewAlert({
            severity: 'information',
            message: 'Settings created successfully!'
          });
          // Redirect to list after successful creation
          setTimeout(() => {
            setLoadingComplete(false);
            setLoading(false);
            navigate('/billing/referrals');
          }, 1000);
        }
      } catch (error) {
        console.error('Error saving settings:', error);
        viewAlert({
          severity: 'error',
          message: isEdit ? 'Failed to update settings. Please try again.' : 'Failed to create settings. Please try again.'
        });
      } finally {
        if (!loadingComplete) {
          setLoading(false);
        }
      }
    },
  });

  // Load data for editing
  useEffect(() => {
    if (isEdit && id) {
      const loadSettings = async () => {
        try {
          setLoadingData(true);
          // Try to get specific setting by ID, fallback to getting all and finding
          let setting;
          try {
            const response = await affiliateService.getSettingById(id);
            setting = response.data;
          } catch {
            // Fallback to getting all settings and finding by ID
            const response = await affiliateService.getSettings();
            setting = response.data.results.find((s: any) => s.id === id);
          }
          
          if (setting) {
            formik.setValues({
              name: setting.name,
              description: setting.description,
              is_default: setting.is_default,
              is_active: setting.is_active,
              manual_referrals_accept: setting.manual_referrals_accept,
              signup_bonus: setting.signup_bonus,
              signup_bonus_value: setting.signup_bonus_value,
              percent_payment: setting.percent_payment,
              signup_required_credit_card: setting.signup_required_credit_card || false,
              signup_required_days: setting.signup_required_days || null,
              signup_minimal_payment: setting.signup_minimal_payment || null,
              percent_minimal_payment: setting.percent_minimal_payment || null,
            });
          } else {
            viewAlert({
              severity: 'error',
              message: 'Settings not found.'
            });
          }
        } catch (error) {
          console.error('Error loading settings:', error);
          viewAlert({
            severity: 'error',
            message: 'Failed to load settings.'
          });
        } finally {
          setLoadingData(false);
        }
      };

      loadSettings();
    }
  }, [id, isEdit]);


  if (loadingData) {
    return (
      <div style={{ padding: '20px' }}>
        <Typography variant="h1" customStyles={{ marginBottom: '20px' }}>
          {isEdit ? 'Edit Affiliate Settings' : 'Create Affiliate Settings'}
        </Typography>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px' }}>
      <div style={{ display: 'flex', flexDirection: 'column',  gap: '16px', marginBottom: '30px' }}>
        <BackArrow fallbackPath="/billing/referrals" />
        <Typography variant="h1">
          {isEdit ? 'Edit Affiliate Settings' : 'Create Affiliate Settings'}
        </Typography>
      </div>


      <form onSubmit={formik.handleSubmit}>
        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Basic Information */}
          <div style={{ display: 'grid', gap: '16px' }}>
            <Typography variant="h3" customStyles={{ marginBottom: '10px' }}>
              Basic Information
            </Typography>
            
            <Input
              title="Name"
              placeholder="Enter affiliate program name"
              fieldName="name"
              values={formik.values}
              handleChange={formik.handleChange}
              touched={formik.touched}
              errors={formik.errors}
              required
            />

            <Input
              title="Description"
              placeholder="Enter program description"
              fieldName="description"
              values={formik.values}
              handleChange={formik.handleChange}
              touched={formik.touched}
              errors={formik.errors}
              isTextArea
              required
            />

            <Toggle
              title="Set as Default"
              value={formik.values.is_default}
              onChange={() => formik.setFieldValue('is_default', !formik.values.is_default)}
            />

            <Toggle
              title="Active"
              value={formik.values.is_active}
              onChange={() => formik.setFieldValue('is_active', !formik.values.is_active)}
            />
          </div>

          {/* Referral Settings */}
          <div style={{ display: 'grid', gap: '16px' }}>
            <Typography variant="h3" customStyles={{ marginBottom: '10px' }}>
              Referral Settings
            </Typography>

            <Toggle
              title="Manual Referrals Accept"
              value={formik.values.manual_referrals_accept}
              onChange={() => formik.setFieldValue('manual_referrals_accept', !formik.values.manual_referrals_accept)}
            />
          </div>

          {/* Signup Bonus Settings */}
          <div style={{ display: 'grid', gap: '16px' }}>
            <Typography variant="h3" customStyles={{ marginBottom: '10px' }}>
              Signup Bonus Settings
            </Typography>

            <Toggle
              title="Enable Signup Bonus"
              value={formik.values.signup_bonus}
              onChange={() => formik.setFieldValue('signup_bonus', !formik.values.signup_bonus)}
            />

            {formik.values.signup_bonus && (
              <Input
                title="Signup Bonus Value"
                placeholder="Enter bonus amount (e.g., 5.00)"
                fieldName="signup_bonus_value"
                values={formik.values}
                handleChange={formik.handleChange}
                touched={formik.touched}
                errors={formik.errors}
                type="text"
                required
              />
            )}

            <Toggle
              title="Require Credit Card for Signup"
              value={formik.values.signup_required_credit_card || false}
              onChange={() => formik.setFieldValue('signup_required_credit_card', !formik.values.signup_required_credit_card)}
            />

            <Input
              title="Required Days (Optional)"
              placeholder="Enter required days"
              fieldName="signup_required_days"
              values={formik.values}
              handleChange={formik.handleChange}
              touched={formik.touched}
              errors={formik.errors}
              type="number"
            />

            <Input
              title="Signup Minimal Payment (Optional)"
              placeholder="Enter minimal payment amount"
              fieldName="signup_minimal_payment"
              values={formik.values}
              handleChange={formik.handleChange}
              touched={formik.touched}
              errors={formik.errors}
              type="number"
            />
          </div>

          {/* Payment Settings */}
          <div style={{ display: 'grid', gap: '16px' }}>
            <Typography variant="h3" customStyles={{ marginBottom: '10px' }}>
              Payment Settings
            </Typography>

            <Input
              title="Payment Percentage"
              placeholder="Enter payment percentage (e.g., 5.00)"
              fieldName="percent_payment"
              values={formik.values}
              handleChange={formik.handleChange}
              touched={formik.touched}
              errors={formik.errors}
              type="text"
              required
            />

            <Input
              title="Percent Minimal Payment (Optional)"
              placeholder="Enter minimal payment percentage"
              fieldName="percent_minimal_payment"
              values={formik.values}
              handleChange={formik.handleChange}
              touched={formik.touched}
              errors={formik.errors}
              type="number"
            />
          </div>

          {/* Submit Buttons */}
          <div style={{ marginTop: '30px', display: 'flex', gap: '12px' }}>
            <Button
              title={isEdit ? 'Update Settings' : 'Create Settings'}
              onClick={() => {}} // Empty onClick since form handles submission
              variant="primary"
              size="mid"
              loading={loading}
              loadingComplete={loadingComplete}
              disabled={!formik.isValid || loading || loadingComplete}
            />
            
            <Button
              title="Cancel"
              onClick={() => navigate('/billing/referrals')}
              variant="secondary"
              size="mid"
              disabled={loading}
            />

            {isEdit && (
              <Button
                title="Reset"
                onClick={() => formik.resetForm()}
                variant="stroke"
                size="mid"
                disabled={loading}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateEditAffiliateSettings;
